import { rewriteLegacyVersioningRequest } from './legacyVersioningRequests';
import {
  addBatch as addCompanyBatch,
  updateOne as updateOneCompany,
} from 'serviceWorker/db/companies/companies';
import { ensureAuthenticatedConfig } from '../config';
import {
  addBatch as addContractBatch,
  updateOne as updateOneContract,
} from 'serviceWorker/db/contracts/contracts';
import { removeBatch as deleteDocuments } from 'serviceWorker/db/documents';
import {
  addBatch as addLevelBatch,
  updateOne as updateOneLevel,
} from 'serviceWorker/db/levels/levels';
import { updateBatch as upsertOrganizationBatch } from 'serviceWorker/db/organizations/organizations';
import {
  updateLocalBatch as upsertProjectBatch,
  updateOne as updateOneProject,
} from 'serviceWorker/db/projects/projects';
import {
  addBatch as addSiteBatch,
  updateOne as updateOneSite,
} from 'serviceWorker/db/sites/sites';
import { updateDeleted as updateInspectionDeleted } from 'serviceWorker/db/inspections/inspections';
import {
  removeChecklistAnswerFromTemplate,
  updateDeleted as updateInspectionTemplateDeleted,
} from 'serviceWorker/db/inspectionTemplates/inspectionTemplates';
import {
  addBatch as addUserBatch,
  getByIds as getUsersByIds,
  removeBatch as removeUserBatch,
  upsertBatch as upsertUserBatch,
} from 'serviceWorker/db/users/users';
import {
  uploadCompany,
  uploadCompanyChanges,
  uploadCompanyDelete,
  uploadCompanyRestore,
} from 'serviceWorker/repository/company/uploadCompany';
import {
  uploadContract,
  uploadContractChanges,
  uploadContractDelete,
  uploadContractRestore,
} from 'serviceWorker/repository/contract/uploadContract';
import { setDirectorySync } from 'serviceWorker/repository/directory/setDirectorySync';
import { uploadDirectory } from 'serviceWorker/repository/directory/uploadDirectory';
import { uploadDirectoryChange } from 'serviceWorker/repository/directory/uploadDirectoryChange';
import { uploadClearDrawing } from 'serviceWorker/repository/document/uploadClearDrawing';
import { uploadDocument } from 'serviceWorker/repository/document/uploadDocument';
import { uploadDocumentChange } from 'serviceWorker/repository/document/uploadDocumentChange';
import { uploadDocumentDelete } from 'serviceWorker/repository/document/uploadDocumentDelete';
import { uploadDrawing } from 'serviceWorker/repository/document/uploadDrawing';
import { setDocumentationSync } from 'serviceWorker/repository/documentation/setDocumentationSync';
import { uploadDocumentation } from 'serviceWorker/repository/documentation/uploadDocumentation';
import {
  uploadDocumentationChange,
  uploadDocumentationVersionChange,
} from 'serviceWorker/repository/documentation/uploadDocumentationChange';
import { uploadDocumentationDelete } from 'serviceWorker/repository/documentation/uploadDocumentationDelete';
import { uploadDocumentationMove } from 'serviceWorker/repository/documentation/uploadDocumentationMove';
import { uploadDocumentationRestore } from 'serviceWorker/repository/documentation/uploadDocumentationRestore';
import {
  uploadLevel,
  uploadLevelChanges,
  uploadLevelDelete,
  uploadLevelRestore,
} from 'serviceWorker/repository/level/uploadLevel';
import {
  uploadOrganization,
  uploadOrganizationChanges,
} from 'serviceWorker/repository/organization/uploadOrganization';
import {
  uploadProject,
  uploadProjectChanges,
  uploadProjectDelete,
  uploadProjectRestore,
} from 'serviceWorker/repository/project/uploadProject';
import {
  uploadSite,
  uploadSiteChanges,
  uploadSiteDelete,
  uploadSiteRestore,
} from 'serviceWorker/repository/site/uploadSite';
import {
  uploadUser,
  uploadUserDelete,
  uploadUserLabelAndPhoneChange,
  uploadUserPermissionsChange,
} from 'serviceWorker/repository/user/uploadUser';
import { uploadVisibleFieldChanges } from 'serviceWorker/repository/visibleField/uploadVisibleField';
import {
  __HttpRequestModel__,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import { OrganizationInDto } from 'shared/domain/organization/types/dto';
import { ProjectInDto } from 'shared/domain/project/types/dto';
import { UserEntity } from 'shared/domain/user/types/entity';
import { CompanyInDto } from 'shared/dtos/in/company';
import { ContractInDto } from 'shared/dtos/in/contract';
import { LevelInDto } from 'shared/dtos/in/level';
import { SiteInDto } from 'shared/dtos/in/site';
import { debugLog } from 'shared/logger/debugLog';
import { guardedByQueuedRequest, runQueuedRequest } from 'serviceWorker/helpers/dataScope';
import { getDirectories, getDirectory } from '../directories';
import { getDocumentation } from '../documentations/getDocumentation';
import { getDocumentations } from '../documentations/getDocumentations';
import { getDocument, updateDocument } from '../documents';
import { sendDocumentUrlBuilder } from './buildDocumentUrl';
import { CompanyRequestRunner } from './company/requestRunner';
import { ContractRequestRunner } from './contractRequestRunner';
import { DirectoryRequestRunner } from './directoryRequestRunner';
import { DocumentationDeleteRequestRunner } from './documentationDeleteRequestRunner';
import { DocumentationMoveRequestRunner } from './documentationMoveRequestRunner';
import { DocumentationRestoreRequestRunner } from './documentationRestoreRequestRunner';
import { DocumentationRequestRunner } from './documentationRequestRunner';
import {
  DocumentRequestRunner,
  DrawingRequestRunner,
} from './documentRequestRunner';
import { fetch } from './fetch';
import { Fetcher } from './fetcher';
import { IssueRequestRunner } from './issue/issueRequestRunner';
import { LevelRequestRunner } from './level/requestRunner';
import { OrganizationRequestRunner } from './organization/requestRunner';
import { ProjectRequestRunner } from './project/requestRunner';
import { ReportRequestRunner } from './reportRequestRunner';
import { SiteRequestRunner } from './site/requestRunner';
import { UserRequestRunner } from './user/requestRunner';
import { VisibleFieldRequestRunner } from './visibleFieldRequestRunner';
import { InspectionRequestRunner } from './inspection/requestRunner';
import { ProtocolItemRequestRunner } from './protocolItem/requestRunner';
import { ProtocolAnswerRequestRunner } from './protocolAnswer/requestRunner';
import { ProtocolSectionRequestRunner } from './protocolSection/requestRunner';
import { ProtocolSectionAnswersBatchRequestRunner } from './protocolSectionAnswersBatch/requestRunner';
import { InspectionTemplateRequestRunner } from './inspectionTemplate/requestRunner';
import { ChecklistSectionRequestRunner } from './checklistSection/requestRunner';
import { ChecklistAnswerRequestRunner } from './checklistAnswer/requestRunner';
import { updateBatch as updateIssueWorkflowBatch } from 'serviceWorker/db/issueWorkflows/issueWorkflows';
import { issueWorkflowsSync } from 'serviceWorker/services/issueWorkflows';
import { WorkflowRequestRunner } from './workflow/requestRunner';
import { StageRequestRunner } from './workflow/stageRequestRunner';
import { DecisionRequestRunner } from './workflow/decisionRequestRunner';
import { ProcessRequestRunner } from './process/requestRunner';
import {
  uploadWorkflow,
  uploadWorkflowChanges,
} from 'serviceWorker/repository/workflow/uploadWorkflow';
import { uploadProcessWorkflowChange } from 'serviceWorker/repository/process/uploadProcess';
import {
  uploadStage,
  uploadStageChanges,
  deleteStage,
  reorderStage,
} from 'serviceWorker/repository/workflow/uploadStage';
import { uploadDecision } from 'serviceWorker/repository/workflow/uploadDecision';

type RunnerMap = Record<HttpRequestModelType, IHttpRequestRunner>;

class HttpRequestRunner implements IHttpRequestRunner {
  constructor(private runnerMap: RunnerMap) {}

  execute(request: __HttpRequestModel__): Promise<any> {
    debugLog('Executing request: ', request);
    const normalized = normalizeLegacyRequest(request);
    return runQueuedRequest(normalized, async () =>
      this.runnerMap[normalized.entityType].execute(await rewriteLegacyVersioningRequest(normalized))
    );
  }
}

/**
 * Backward-compat shim for queued requests created before
 * `workflowStageReorder` entityType was introduced. Such requests were stored
 * as `workflowStage` with `data.stageReorderDto` and need to be re-routed to
 * the dedicated entity type so dispatch is unambiguous.
 */
function normalizeLegacyRequest(
  request: __HttpRequestModel__
): __HttpRequestModel__ {
  if (
    request.entityType === HttpRequestModelType.workflowStage &&
    request.data &&
    'stageReorderDto' in request.data
  ) {
    return {
      ...request,
      entityType: HttpRequestModelType.workflowStageReorder,
    };
  }
  return request;
}

const documentationRequestRunner = new DocumentationRequestRunner(
  Fetcher,
  uploadDocumentation,
  uploadDocumentationChange,
  uploadDocumentationVersionChange,
  getDocumentation
);

/**
 * Adapter for Fetcher to implement IStageUploader interface.
 * Splits ambiguous PUT method into type-safe UPDATE and REORDER methods.
 */
const stageUploaderAdapter = {
  POST: Fetcher.POST,
  UPDATE: Fetcher.PUT,
  REORDER: Fetcher.PUT,
  DELETE: Fetcher.DELETE,
};

// Read at call time: the HTTP queue imports the workflows module while it may still load.
const syncIssueWorkflows = (): void => issueWorkflowsSync.sync();

const stageRequestRunner = new StageRequestRunner(
  stageUploaderAdapter,
  uploadStage,
  uploadStageChanges,
  deleteStage,
  reorderStage,
  syncIssueWorkflows
);

export const httpRequestRunner = new HttpRequestRunner({
  [HttpRequestModelType.document]: new DocumentRequestRunner(
    Fetcher,
    getDocument,
    guardedByQueuedRequest(deleteDocuments),
    sendDocumentUrlBuilder,
    uploadDocument,
    uploadDocumentChange,
    uploadDocumentDelete,
    guardedByQueuedRequest(updateDocument)
  ),
  [HttpRequestModelType.drawing]: new DrawingRequestRunner(
    Fetcher,
    getDocument,
    sendDocumentUrlBuilder,
    uploadDrawing,
    uploadClearDrawing
  ),
  [HttpRequestModelType.issue]: new IssueRequestRunner(ensureAuthenticatedConfig, fetch),
  [HttpRequestModelType.report]: new ReportRequestRunner(
    ensureAuthenticatedConfig,
    fetch
  ),
  [HttpRequestModelType.documentation]: documentationRequestRunner,
  [HttpRequestModelType.documentationVersion]: documentationRequestRunner,
  [HttpRequestModelType.level]: new LevelRequestRunner(
    Fetcher,
    uploadLevel,
    guardedByQueuedRequest((levelInDto: LevelInDto) => addLevelBatch([levelInDto])),
    uploadLevelChanges,
    uploadLevelDelete,
    guardedByQueuedRequest((levelId: string) => updateOneLevel(levelId, { deleted: 1 })),
    uploadLevelRestore,
    guardedByQueuedRequest((levelId: string) => updateOneLevel(levelId, { deleted: 0 }))
  ),
  [HttpRequestModelType.directory]: new DirectoryRequestRunner(
    Fetcher,
    uploadDirectory,
    uploadDirectoryChange,
    getDirectory
  ),
  [HttpRequestModelType.documentationMove]:
    new DocumentationMoveRequestRunner(
      Fetcher,
      uploadDocumentationMove,
      getDocumentations,
      getDirectories
    ),
  [HttpRequestModelType.documentationDelete]:
    new DocumentationDeleteRequestRunner(
      Fetcher,
      uploadDocumentationDelete,
      getDocumentations,
      getDirectories,
      setDocumentationSync,
      setDirectorySync
    ),
  [HttpRequestModelType.documentationRestore]:
    new DocumentationRestoreRequestRunner(
      Fetcher,
      uploadDocumentationRestore,
      getDocumentations,
      getDirectories,
      setDirectorySync
    ),
  [HttpRequestModelType.site]: new SiteRequestRunner(
    Fetcher,
    uploadSite,
    guardedByQueuedRequest((siteInDto: SiteInDto) => addSiteBatch([siteInDto])),
    uploadSiteChanges,
    uploadSiteDelete,
    guardedByQueuedRequest((siteId: string) => updateOneSite(siteId, { deleted: 1 })),
    uploadSiteRestore,
    guardedByQueuedRequest((siteId: string) => updateOneSite(siteId, { deleted: 0 }))
  ),
  [HttpRequestModelType.visibleField]: new VisibleFieldRequestRunner(
    Fetcher,
    uploadVisibleFieldChanges
  ),
  [HttpRequestModelType.project]: new ProjectRequestRunner(
    Fetcher,
    uploadProject,
    (projectInDto: ProjectInDto) => upsertProjectBatch([projectInDto]),
    uploadProjectChanges,
    uploadProjectDelete,
    (projectId: string) => updateOneProject(projectId, { deleted: 1 }),
    uploadProjectRestore,
    (projectId: string) => updateOneProject(projectId, { deleted: 0 })
  ),
  [HttpRequestModelType.organization]: new OrganizationRequestRunner(
    Fetcher,
    uploadOrganization,
    (organizationInDto: OrganizationInDto) =>
      upsertOrganizationBatch([organizationInDto]),
    uploadOrganizationChanges
  ),
  [HttpRequestModelType.company]: new CompanyRequestRunner(
    Fetcher,
    uploadCompany,
    guardedByQueuedRequest((companyInDto: CompanyInDto) => addCompanyBatch([companyInDto])),
    uploadCompanyChanges,
    uploadCompanyDelete,
    guardedByQueuedRequest((companyId: string) => updateOneCompany(companyId, { deleted: 1 })),
    uploadCompanyRestore,
    guardedByQueuedRequest((companyId: string) => updateOneCompany(companyId, { deleted: 0 }))
  ),
  [HttpRequestModelType.contract]: new ContractRequestRunner(
    Fetcher,
    uploadContract,
    guardedByQueuedRequest((contractInDto: ContractInDto) => addContractBatch([contractInDto])),
    uploadContractChanges,
    uploadContractDelete,
    guardedByQueuedRequest((contractId: string) => updateOneContract(contractId, { deleted: 1 })),
    uploadContractRestore,
    guardedByQueuedRequest((contractId: string) => updateOneContract(contractId, { deleted: 0 }))
  ),
  [HttpRequestModelType.user]: new UserRequestRunner(
    Fetcher,
    uploadUser,
    guardedByQueuedRequest((userEntities: UserEntity[]) => upsertUserBatch(userEntities)),
    getUsersByIds,
    uploadUserLabelAndPhoneChange,
    uploadUserPermissionsChange,
    uploadUserDelete,
    guardedByQueuedRequest((userId: string) => removeUserBatch([userId]))
  ),
  [HttpRequestModelType.inspection]: new InspectionRequestRunner(
    Fetcher,
    guardedByQueuedRequest(updateInspectionDeleted)
  ),
  [HttpRequestModelType.protocolItem]: new ProtocolItemRequestRunner(
    Fetcher
  ),
  [HttpRequestModelType.protocolAnswer]: new ProtocolAnswerRequestRunner(
    Fetcher
  ),
  [HttpRequestModelType.protocolSection]: new ProtocolSectionRequestRunner(
    Fetcher
  ),
  [HttpRequestModelType.protocolSectionAnswersBatch]:
    new ProtocolSectionAnswersBatchRequestRunner(Fetcher),
  [HttpRequestModelType.inspectionTemplate]: new InspectionTemplateRequestRunner(
    Fetcher,
    guardedByQueuedRequest(updateInspectionTemplateDeleted)
  ),
  [HttpRequestModelType.checklistSection]: new ChecklistSectionRequestRunner(
    Fetcher
  ),
  [HttpRequestModelType.checklistSectionDocument]:
    new ChecklistSectionRequestRunner(Fetcher),
  [HttpRequestModelType.checklistAnswer]: new ChecklistAnswerRequestRunner(
    Fetcher,
    guardedByQueuedRequest(removeChecklistAnswerFromTemplate)
  ),
  [HttpRequestModelType.workflow]: new WorkflowRequestRunner(
    Fetcher,
    uploadWorkflow,
    uploadWorkflowChanges,
    guardedByQueuedRequest((workflow) => updateIssueWorkflowBatch([workflow])),
    syncIssueWorkflows
  ),
  [HttpRequestModelType.workflowStage]: stageRequestRunner,
  [HttpRequestModelType.workflowStageReorder]: stageRequestRunner,
  [HttpRequestModelType.workflowDecision]: new DecisionRequestRunner(
    Fetcher,
    uploadDecision
  ),
  [HttpRequestModelType.process]: new ProcessRequestRunner(
    Fetcher,
    uploadProcessWorkflowChange
  ),
});
